/**
 * WarehouseQuickCreateDialog - Minimal dialog for quick warehouse creation
 *
 * Following the Quick Create Dialog pattern:
 * - 3 fields: Name, Code, Type
 * - Redirects to detail page after creation
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { cn } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { CompanyPicker, DialogForm } from '../../../shared/components/common';
import { useCreateWarehouse } from '../hooks/useWarehouses';
import { useApiError } from '../../../shared/hooks/useApiError';
import type { Warehouse, WarehouseType } from '../types';
import { InventoryWarehouseType } from '../../../generated/api/models/inventoryWarehouseType';

const WAREHOUSE_TYPES: WarehouseType[] = Object.values(InventoryWarehouseType);

interface WarehouseQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (warehouse: Warehouse) => void;
  skipNavigation?: boolean;
  /** Pre-fill the name field (e.g. the text typed into a WarehousePicker) */
  defaultName?: string;
}

interface QuickCreateFormData {
  name: string;
  code: string;
  type: WarehouseType;
  company_id: string | null;
}

export function WarehouseQuickCreateDialog({
  open,
  onClose,
  onCreated,
  skipNavigation = false,
  defaultName = '',
}: WarehouseQuickCreateDialogProps) {
  const { t } = useTranslation(['inventory', 'common']);
  const navigate = useNavigate();
  const createMutation = useCreateWarehouse();
  const { handleMutationError } = useApiError({ showToast: false });

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    mode: 'onBlur',
    defaultValues: {
      name: defaultName,
      code: '',
      type: 'warehouse',
      company_id: null,
    },
  });

  useEffect(() => {
    if (open) {
      reset({ name: defaultName, code: '', type: 'warehouse', company_id: null });
    }
  }, [open, reset, defaultName]);

  const onSubmit = async (data: QuickCreateFormData) => {
    try {
      const newWarehouse = await createMutation.mutateAsync({
        name: data.name,
        code: data.code,
        type: data.type,
        company_id: data.company_id || undefined,
      });
      onCreated?.(newWarehouse);
      onClose();

      if (!skipNavigation) {
        navigate(`/inventory/warehouses/${newWarehouse.id}`);
      }
    } catch (error) {
      handleMutationError(error, setError);
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('warehouses.create')}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={t('common:app.create')}
      cancelLabel={t('common:app.cancel')}
      isLoading={isSubmitting}
      maxWidth="sm"
    >
      <div className="space-y-4">
        <Controller
          name="company_id"
          control={control}
          render={({ field }) => (
            <CompanyPicker
              value={field.value}
              onChange={field.onChange}
              label={t('common:multiCompany.company', 'Company')}
              required
            />
          )}
        />
        <div className="space-y-2">
          <Label htmlFor="warehouse-name">{t('warehouses.name')} *</Label>
          <Controller
            name="name"
            control={control}
            rules={{ required: t('common:validation.required') }}
            render={({ field }) => (
              <Input
                {...field}
                id="warehouse-name"
                autoFocus
                autoComplete="off"
                data-1p-ignore
                data-lpignore="true"
                placeholder={t('warehouses.namePlaceholder')}
                className={cn(errors.name && 'border-destructive')}
              />
            )}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name.message}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="warehouse-code">{t('warehouses.code')} *</Label>
            <Controller
              name="code"
              control={control}
              rules={{ required: t('common:validation.required') }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="warehouse-code"
                  autoComplete="off"
                  data-1p-ignore
                  data-lpignore="true"
                  placeholder={t('warehouses.codePlaceholder')}
                  className={cn(errors.code && 'border-destructive')}
                />
              )}
            />
            {errors.code && (
              <p className="text-sm text-destructive">{errors.code.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>{t('warehouses.type')}</Label>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className={cn(errors.type && 'border-destructive')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WAREHOUSE_TYPES.map((wType) => (
                      <SelectItem key={wType} value={wType}>
                        {t(`warehouses.types.${wType}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.type && (
              <p className="text-sm text-destructive">{errors.type.message}</p>
            )}
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          {t('warehouses.quickCreateHint')}
        </p>
      </div>
    </DialogForm>
  );
}
